import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ClaySubscription,
  EntitlementName,
  ProductName,
  UserEntitlements,
  UserProfileRequest,
  UserSession,
} from '@app/types';
import { setUser as setUserInStore } from '@reducers/user/account';
import buildFormData from '@support/buildFormData';
import { useDispatch, useSelector } from 'react-redux';
import {
  cleanupAndLogout,
  clearNewSignUp,
  isAccessTokenValid,
  isNewMarketingSignUp,
  isRefreshTokenValid,
} from '@support/jwt';
import * as Sentry from '@sentry/react';
import analytics from '@support/analytics';
import { sentryKey } from '@app/config';
import { redirectLocation } from '@actions/ui';
import {
  clearRewardfulAffiliateCode,
  getRewardfulAffiliateCode,
  submitNewUserUTMParams,
} from '@support/utm';
import { appVersion } from '@app/config';
import { Traits } from '@segment/analytics-next';
import { apiRequest, checkUser } from '@support/api';
import hideLoader from '@support/hide-loader';
import { isPublicRoute } from '@app/Routes/AppRoutes';
import Contact from '@models/Contact';
import { AuthContext, AuthContextSchema } from './AuthContext';
import { usePostHog } from 'posthog-js/react';
import { selectUserContactCount } from '@reducers/user/stats';

const userTraits = (user: Partial<UserSession> | null): Traits | undefined => {
  if (!user) {
    return undefined;
  }

  const traits: Traits = {
    id: user.id,
    first_name: user.first_name,
    last_name: user.last_name,
    username: user.username,
    email: user.email,
    web_version: appVersion,
    name: `${user.first_name} ${user.last_name}`.trim(),
  };

  if (window.app.isElectron && window.desktopIntegrations) {
    traits.desktop_version = window.desktopIntegrations.appInfo.version;
  }

  return traits;
};

export function AuthProvider({ children }: PropsWithChildren<{}>) {
  const dispatch = useDispatch();
  const posthog = usePostHog();
  const [user, setUser] = useState<UserSession | null>(null);
  const [entitlements, setEntitlements] = useState<UserEntitlements>({});
  const [subscriptions, setSubscriptions] = useState<ClaySubscription[]>([]);
  // const [onboarded, setOnboarded] = useState(user?.has_completed_onboarding);
  const [ready, setReady] = useState(false);
  const [accountProcessingStatus, setAccountProcessingStatus] = useState(null);
  const userContactCount = useSelector(selectUserContactCount);
  const contactLimit: number | null = useMemo(() => entitlements['contact.limit'], [entitlements]);

  const contactIsRestricted = useCallback(
    (contact: Contact) =>
      userContactCount && contactLimit && userContactCount > contactLimit && contact.isRestricted,
    [contactLimit, userContactCount],
  );

  useEffect(() => {
    if (!ready) {
      // get our user and user's account status
      (async () => {
        const userData = await pullUserData();

        if (!userData && !isPublicRoute(window.location.pathname)) {
          window.location.href = '/login';
          return;
        }

        // We only want to check account status if the user is logged in
        if (userData) {
          await pullAccountStatus();
          await pullSubscriptions();
          await pullEntitlements();
        }

        hideLoader();
        setReady(true);
        // isNewSignUp checks for marketing sign up flow
        // has_completed_onboarding is used to grab users that created accounts outside that flow
        if (userData && (isNewMarketingSignUp() || !userData.has_completed_onboarding)) {
          await submitNewUserUTMParams(userData);

          // only redirect for new marketing sign up
          if (isNewMarketingSignUp()) {
            analytics.googleTrackConversion();
            await submitRewardfulReferral();
            clearNewSignUp();
            if (!window.app.isElectron && !userData.has_completed_onboarding) {
              posthog?.identify(String(userData?.id));
              posthog?.set_config({ autocapture: true });
              posthog?.startSessionRecording();
              dispatch(redirectLocation({ pathname: '/start' }));
            }
          }
        }
      })();
    }
  }, [ready]);

  useEffect(() => {
    window.app.on('window:focus', onFocus);

    return () => {
      window.app.off('window:focus', onFocus);
    };
  }, [user]);

  useEffect(() => {
    if (user && sentryKey) {
      Sentry.setUser({
        id: String(user.id),
        email: user.email,
      });
    }

    if (user && window.app.isElectron && window.desktopIntegrations) {
      window.desktopIntegrations.userAuthenticated();
      if (window.desktopIntegrations.setScopes) {
        window.desktopIntegrations.setScopes({
          userScopes: {
            id: `${user.id}`,
            email: user.email,
          },
        });
      }
    }

    analytics.identify(user, userTraits(user));
  }, [user]);

  async function pullUserData(): Promise<UserSession | null> {
    if (isAccessTokenValid() || isRefreshTokenValid()) {
      try {
        const userData = await checkUser();
        if (JSON.stringify(user) !== JSON.stringify(userData)) {
          setUser(userData);
        }
        return userData;
      } catch (e) {
        // we shouldn't have to do anything - we will be redirected
        if (!!e.status || e.status !== 401) {
          console.error(e);
        }
      }
    }
    return null;
  }

  async function pullAccountStatus() {
    const accountStatus = await apiRequest<{
      enrichment: {
        total: number;
        processed: number;
      };
      status: string;
    }>('GET', '/users/self/status/');
    setAccountProcessingStatus(accountStatus?.status);
    return accountStatus?.status;
  }

  async function pollForAccountStatus() {
    const status = await pullAccountStatus();
    if (status !== 'complete') {
      setTimeout(pollForAccountStatus, 5000);
    }
  }

  async function pullSubscriptions() {
    const subscriptions = await apiRequest<{ results: ClaySubscription[] }>(
      'GET',
      '/subscriptions/',
      undefined,
      {
        apiVersion: 'v2',
      },
    );
    setSubscriptions(subscriptions?.results);
  }

  async function pullEntitlements() {
    if (isAccessTokenValid() || isRefreshTokenValid()) {
      const userEntitlementList = await apiRequest<
        {
          entitlement: EntitlementName;
          value: number | boolean | null;
        }[]
      >('GET', '/self/entitlements/', undefined, {
        apiVersion: 'v2',
      });

      let entitlements = userEntitlementList?.reduce(
        (es, e) => ({ ...es, [e.entitlement]: e.value }),
        {},
      );
      // To perform overrides locally, uncomment the following line and edit as needed. We should add manual
      // editing to the settings staging panel when there is time.
      // entitlements = { ...entitlements, 'contact.limit': 750 }
      setEntitlements(entitlements);
      return;
    }
    setEntitlements({});
  }

  function onFocus() {
    if (user) {
      analytics.track('Application Opened');
      analytics.screen('Application Opened');
    }

    pullUserData();
    pullEntitlements();
  }

  function updateLocalUserProfile(userProfile: UserSession): void {
    setUser({ ...user, ...userProfile });
  }

  async function updateUserProfile(userProfile: UserProfileRequest): Promise<boolean> {
    const updatedUser = await apiRequest<UserSession>(
      'PATCH',
      '/users/self/',
      buildFormData(userProfile),
    );

    if (updatedUser) {
      updateLocalUserProfile(updatedUser);
      // if (!onboarded && updatedUser.has_completed_onboarding) {
      //   setOnboarded(true);
      // }
      return true;
    }

    return false;
  }

  function logout(): void {
    setUser(null);
    cleanupAndLogout();
  }

  // entitlement values can be a boolean, number, or null.
  // Returning undefined if the entitlement does not exist.
  function getEntitlementValue(
    entitlementName: EntitlementName,
  ): number | boolean | null | undefined {
    if (entitlements.hasOwnProperty(entitlementName)) {
      return entitlements[entitlementName];
    }
    return undefined;
  }

  async function submitRewardfulReferral(): Promise<void> {
    const via = getRewardfulAffiliateCode();
    if (!via) {
      return;
    }

    try {
      await apiRequest(
        'POST',
        '/stripe-referral/',
        {
          referral: via,
        },
        {
          apiVersion: 'v2',
        },
      );
    } catch (e) {
      console.error(e);
    }
    clearRewardfulAffiliateCode();
  }

  function setUserPrimaryProduct(productName: ProductName): void {
    dispatch(setUserInStore({ ...user, primary_product: { name: productName } }));
  }

  useEffect(() => {
    dispatch(
      setUserInStore({ ...user, contactLimit: getEntitlementValue('contact.limit') as number }),
    );
  }, [user, entitlements]);

  // useEffect(() => {
  //   if (onboarded && location.pathname == '/onboarding') {
  //     dispatch(redirectLocation({ pathname: '/tour' }));
  //   }
  // }, [onboarded]);

  const value: AuthContextSchema = {
    user,
    isReady: ready,
    subscriptions,
    accountProcessingStatus,
    pollForAccountStatus,
    updateLocalUserProfile,
    updateUserProfile,
    getEntitlementValue,
    setUserPrimaryProduct,
    cleanupAndLogout: logout,
    getIsFreePlan: () => user && user.primary_product.name === 'Clay Personal',
    getIsProPlan: () => user && user.primary_product.name === 'Clay Pro',
    getIsTeamsPlan: () => user && user.primary_product.name === 'Clay Teams',
    fetchUserData: pullUserData,
    contactLimit,
    contactIsRestricted,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
